import { CSSInterpolation } from '@emotion/css/create-instance'
import type { PhoneNumber } from 'libphonenumber-js'
import {
  AsYouType,
  type CountryCode,
  getExampleNumber,
  parseDigits,
  parsePhoneNumberFromString,
} from 'libphonenumber-js'
import React, {
  type FocusEvent,
  type MutableRefObject,
  type Ref,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import type { ReactInputProps } from '../../types'
import { SCREENREADER_ONLY_STYLES } from '../../util/commonStyles'
import {
  countries,
  type CountryCode as InternalCountryCode,
} from '../../util/countries'
import { CssVars, NeutralColorToken, SemanticColorToken } from '../themes'
import { type InputState, InternalInput } from './internal/InternalInput'
import { InternalInputWrapper } from './internal/InternalInputWrapper'
import type { MenuOptionVariantSelectable } from './MenuOption'
import { SelectMenu } from './SelectMenu'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

import phoneNumberExamples from 'libphonenumber-js/examples.mobile.json'

const DEFAULT_COUNTRY_CODE: CountryCode = 'US'

const LABEL_COLOR_FOR_STATE = {
  default: undefined,
  disabled: NeutralColorToken.Fg2,
  error: SemanticColorToken.Error,
}

type ValueType = {
  value: string
  countryCode: CountryCode
}

type Props = {
  /**
   * Optional array of supported country codes and their labels (should be translated).
   * By default this input will only support US phone numbers.
   * If this array is provided, a select dropdown will be provided to choose country code in the order of the array provided.
   */
  countryOptions?: Array<{
    countryCode: CountryCode
    countryName: string
  }> | null
  /**
   * An error message to show below the input. Will only be shown if `state` is `"error"`.
   */
  errorMessage?: string
  /**
   * Provide `true` to visually hide the input label, while still leaving it present in the DOM
   * for screen readers. Ignored if no `label` is provided.
   */
  hideLabel?: boolean
  /**
   * A React ref to attach to the actual `input` element, e.g. for triggering focus or blur on
   * the input.
   */
  innerRef?: Ref<HTMLInputElement>
  /**
   * A label to show above the input. If you would like to visually hide it please use
   * `hideLabel` prop to do so. If you do not provide a value here, you must provide your own
   * `<label>` element for the input for accessibility reasons.
   */
  label?: string
  /**
   * Callback that will be fired when input is blurred.
   */
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  /**
   * Callback that will be fired whenever the input changes with raw value and selected country code.
   */
  onChange: (data: ValueType) => void

  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation

  /**
   * Placeholder text to show when the input is empty. If not specified, a default placeholder
   * for a phone number of the country specified in `value` will be shown.
   */
  placeholder?: ReactInputProps['placeholder']

  /**
   * Change the value returned to include '+' symbol in front of value.
   */
  prefixValueWithPlus?: boolean

  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: InputState

  /**
   * The value to show in the input and the country to show in the select.
   * If empty, default placeholder for a phone number of the default country (US) is shown.
   */
  value: ValueType
}

function getCountryCallingCode(countryCode: CountryCode) {
  // Default to US if country code is not found
  return (
    countries[countryCode as InternalCountryCode]?.phoneNumberCountryCode ?? '1'
  )
}

function removeWhiteSpaceFromNumber(val: string) {
  return val.replace(/\s+/g, '')
}

function cleanFormattedPhoneNumber(val: string) {
  return val.replace(/[^\d]/g, '')
}

function getPrefixColorToken(
  value: string,
  isFocused: boolean,
  isDisabled: boolean
) {
  if (isDisabled) {
    return NeutralColorToken.Fg3
  }

  if (!value.length) {
    return isFocused ? NeutralColorToken.Fg3 : NeutralColorToken.Fg2
  }

  return undefined
}

function formatInternationalPhoneNumber(
  value: string,
  countryCode: CountryCode,
  prefixValueWithPlus?: boolean
) {
  const phoneNumber = parsePhoneNumberFromString(
    parseDigits(value),
    countryCode
  )

  if (phoneNumber) {
    if (!prefixValueWithPlus) {
      return phoneNumber.formatInternational().replace('+', '')
    }

    return phoneNumber.formatInternational()
  }

  return value
}

function removeLeadingCountryCallingCode(
  value: string,
  countryCode: CountryCode
) {
  return value.replace(
    // eslint-disable-next-line security/detect-non-literal-regexp
    new RegExp(`^\\+?${getCountryCallingCode(countryCode)}`),
    ''
  )
}

function formatPhoneNumber(value: string, countryCode: CountryCode) {
  const phoneNumber: PhoneNumber | undefined = parsePhoneNumberFromString(
    parseDigits(value),
    countryCode
  )

  /**
   * Always use international format since {@link PhoneNumber.formatNational} prepends "0" for
   * some international numbers
   */
  const internationalValueFormat = phoneNumber?.formatInternational() || value
  const asYouType = new AsYouType(countryCode)

  return asYouType.input(
    removeLeadingCountryCallingCode(internationalValueFormat, countryCode)
  )
}

function getPhoneNumberNormalizerByCountryCode(countryCode: CountryCode) {
  return (val: string, previousVal: string) => {
    if (!val) {
      return ''
    }

    const cleanedVal = cleanFormattedPhoneNumber(val)
    const cleanedPreviousVal = cleanFormattedPhoneNumber(previousVal)

    const isTypingBackwards =
      previousVal != null && previousVal.length > val.length

    if (isTypingBackwards && cleanedVal === cleanedPreviousVal) {
      // Special logic to remove parenthesis
      if (previousVal.trim().endsWith(')')) {
        return val.replace('(', '')
      }

      return val
    }

    return formatPhoneNumber(cleanedVal, countryCode)
  }
}

/**
 * An input element for inputting phone numbers and a select element for selecting the country.
 * It only accepts numeric characters. Under the hood, this uses `libphonenumber-js` to power
 * the formatting of the phone number.
 *
 * Like all inputs, this should be used inside a `<form>` element. You also may want to use
 * the `getInputState` helper.
 *
 * For country options. please take a look at `src/util/countries/index.tsx`, any non-supported countries
 * will result in unformatted phone number
 *
 * WARNING: very expensive rerenders occur every time the props onChange and countryOptions change in identity.
 * You should ensure they do not change, for example you may need to use {@link useMemo} or {@link useCallback}.
 */
export function PhoneNumberInput({
  countryOptions,
  errorMessage = '',
  hideLabel = false,
  innerRef,
  label,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  overrideRootStyles,
  placeholder,
  prefixValueWithPlus = false,
  state = 'default',
  value: valueProp,
}: Props) {
  const previousSelectionStart = useRef<number | null>(0)
  // To keep track of internal value
  const [internalValue, setInternalValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  // Note(auster.chen) since we also need a reference to the input in this implementation
  // we hack the `innerRef` pass through
  const setInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element

      if (typeof innerRef === 'function') {
        innerRef(element)
      } else if (innerRef) {
        ;(innerRef as MutableRefObject<HTMLInputElement | null>).current =
          element
      }
    },
    [innerRef]
  )
  const [countryCode, setCountryCode] = useState<CountryCode>(
    valueProp?.countryCode ?? DEFAULT_COUNTRY_CODE
  )
  const hasCountrySelector = countryOptions && countryOptions.length > 0
  const normalizer = useMemo(
    () => getPhoneNumberNormalizerByCountryCode(countryCode),
    [countryCode]
  )

  const onChange = ({ value: newValue }: { value: string }) => {
    // if we're editing the middle, maintain cursor position
    if ((inputRef.current?.selectionStart || 0) < newValue.length - 1) {
      previousSelectionStart.current =
        typeof inputRef.current?.selectionStart === 'number'
          ? inputRef.current?.selectionStart
          : 0
    } else {
      previousSelectionStart.current = null
    }

    const formattedValue = normalizer(newValue, internalValue)

    setInternalValue(formattedValue)

    onChangeProp({
      countryCode,
      value: removeWhiteSpaceFromNumber(
        formatInternationalPhoneNumber(
          cleanFormattedPhoneNumber(formattedValue),
          countryCode,
          prefixValueWithPlus
        )
      ),
    })
  }

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    const formattedValue = normalizer(internalValue, internalValue)
    setIsFocused(false)

    if (formattedValue !== internalValue) {
      setInternalValue(formattedValue)
    }

    onBlurProp?.(event)
  }

  // valuePropRef allows the usage of valueProp within useCallbacks and useMemo without excessive re-renders
  const valuePropRef = useRef(valueProp)
  valuePropRef.current = valueProp

  // useCallback memoizes the function to help prevent unnecessary renders of countryCodeDropdown, which is
  // incredibly laggy when rendering the >250 countries.
  const onSelectChange = useCallback(
    (option: MenuOptionVariantSelectable<CountryCode>) => {
      const newCountryCode = option.value as CountryCode
      setCountryCode(newCountryCode)
      // whenever we change country code, do an automatic re-format but need to remove the existing country code
      const asYouType = new AsYouType(newCountryCode)
      const newValue = asYouType.input(
        cleanFormattedPhoneNumber(valuePropRef.current.value)
      )

      onChangeProp?.({
        countryCode: newCountryCode,
        value: removeWhiteSpaceFromNumber(
          formatInternationalPhoneNumber(
            removeLeadingCountryCallingCode(newValue, countryCode),
            newCountryCode,
            prefixValueWithPlus
          )
        ),
      })

      inputRef.current?.focus()
    },
    [setCountryCode, onChangeProp, prefixValueWithPlus, countryCode]
  )

  // Note(auster.chen): this is used for label and input to be aware of each other
  // as we don't have nesting here.
  const inputId = `phone-input-${useUniqueId()}`

  useLayoutEffect(() => {
    if (previousSelectionStart.current !== null) {
      inputRef.current?.setSelectionRange(
        previousSelectionStart.current,
        previousSelectionStart.current
      )
    }
  }, [internalValue])

  useEffect(() => {
    if (valueProp.countryCode !== countryCode) {
      setCountryCode(valueProp.countryCode)
    }

    // Only update internal value if the underlying value is different
    if (
      parseDigits(
        removeLeadingCountryCallingCode(valueProp.value, valueProp.countryCode)
      ) !== parseDigits(cleanFormattedPhoneNumber(internalValue))
    ) {
      setInternalValue(
        formatPhoneNumber(valueProp.value, valueProp.countryCode)
      )
    }
  }, [countryCode, internalValue, valueProp])

  // Memoized as it is a very laggy component with hundreds of internal elements.
  const countryCodeDropdown = useMemo(() => {
    return (
      <SelectMenu<MenuOptionVariantSelectable<CountryCode>>
        aria-describedby="bento-country-select"
        aria-label="Country select"
        menuWidth={200}
        onChange={onSelectChange}
        options={
          countryOptions?.map(({ countryCode: _countryCode, countryName }) => ({
            controlLabel: _countryCode,
            label: `${countryName} (+${getCountryCallingCode(_countryCode)})`,
            value: _countryCode as CountryCode,
          })) ?? []
        }
        overrideRootStyles={{
          marginRight: CssVars.size.gutterWidthSm,
        }}
        // don't error out select menu field as that doesn't make sense
        state={state === 'disabled' ? 'disabled' : 'default'}
        value={countryCode}
      />
    )
  }, [onSelectChange, countryOptions, state, countryCode])

  const inputPlaceholder = useMemo(() => {
    if (placeholder) {
      return placeholder
    }

    return getExampleNumber(countryCode, phoneNumberExamples)
      ?.formatNational()
      .replace(/\d/g, 'X')
  }, [countryCode, placeholder])

  return (
    <InternalInputWrapper
      errorMessage={errorMessage}
      overrideRootStyles={overrideRootStyles}
      state={state}
    >
      {errorMessageId => (
        <div
          css={[
            {
              display: 'flex',
              flexDirection: 'column',
            },
          ]}
        >
          {
            // TODO(eric.lovett): Find a way for this label to be handled by
            // InternalInputWrapper rather than reimplemented here.
            label && (
              <div
                css={
                  hideLabel
                    ? SCREENREADER_ONLY_STYLES
                    : { marginBottom: CssVars.size.gutterWidthSm }
                }
              >
                <Type.Text
                  color={state && LABEL_COLOR_FOR_STATE[state]}
                  size="S"
                >
                  <label htmlFor={inputId}>{label}</label>
                </Type.Text>
              </div>
            )
          }
          <div
            css={{
              display: 'flex',
            }}
          >
            {hasCountrySelector && (
              <>
                <span css={SCREENREADER_ONLY_STYLES} id="bento-country-select">
                  Currently selected phone number country is{' '}
                  {
                    countryOptions?.find(
                      option => option.countryCode === countryCode
                    )?.countryName
                  }
                </span>
                {countryCodeDropdown}
              </>
            )}
            <InternalInput
              aria-describedby={errorMessageId}
              id={inputId}
              innerRef={setInputRef}
              maxLength={20}
              onBlur={onBlur}
              onChange={onChange}
              onFocus={() => {
                setIsFocused(true)
              }}
              placeholder={inputPlaceholder}
              startControl={
                hasCountrySelector ? (
                  <Type.Text
                    color={getPrefixColorToken(
                      valueProp.value,
                      isFocused,
                      state === 'disabled'
                    )}
                    overrideRootStyles={{
                      cursor: 'default',
                      marginRight: 2,
                    }}
                    size="S"
                  >
                    +{getCountryCallingCode(countryCode)}
                    &nbsp;
                  </Type.Text>
                ) : null
              }
              state={state}
              type="tel"
              value={internalValue}
            />
          </div>
        </div>
      )}
    </InternalInputWrapper>
  )
}
